import { useFiltersByScreen } from '@/components_v2/Filtersv3/filter-store';
import { FragmentType, gql } from '@/gql';
import { useAuth } from '@/hooks/Authentication/auth';
import { NotificationsTopics } from '@/hooks/Notifications/utils/NotificationsTopics';
import { reduceNestedFilters } from '@/utils/filters';
import { createContext } from '@/utils/react-utils';
import { useQuery } from '@apollo/client';
import React from 'react';

export const exportFragment = gql(`
  fragment ExportFragment on Export {
    id
    progress
    titleEvent
    status
    updatedAt
    exportType
    dateStart
    dateEnd
    parentableType,
    parentableId,
    url,
    author {
      id
      email
      fullName
    }
    exportWithErrors
    exportErrors
    token
    pivoted
    createdAt
    exportColumns
    emailsForSend
  }
`);

export const exportQuery = gql(`
  query Exports($after: String, $before: String, $first: Int, $last: Int, $filters: JSON, $formularyIds: [ID!], $formularyGroupIds: [ID!], $exportType: [String!]) {
    exports(after: $after, before: $before, first: $first, last: $last, filters: $filters, formularyIds: $formularyIds, formularyGroupIds: $formularyGroupIds, exportType: $exportType) {
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      edges {
        cursor
        node {
          id
          ...ExportFragment
        }
      }
    }
  }
`);

export type ExportFragmentType = FragmentType<typeof exportFragment>;

export function useExportsController() {
  const { user } = useAuth();
  const { byKey } = useFiltersByScreen();

  // Memoize filter processing to avoid recalculation on every render
  const processedFilters = React.useMemo(() => {
    const reduced = reduceNestedFilters(byKey);

    // Extract parameters that go outside filters
    const {
      formularyIds,
      formularyGroupIds,
      exportType,
      ...otherFilters
    } = reduced;

    // Handle created_at filter like in Tickets screen
    const cleanFilters = { ...otherFilters };
    const createdAtFilter = cleanFilters.created_at
      ? JSON.parse(cleanFilters.created_at)
      : {};
    delete cleanFilters.created_at;

    // Add created_at to exports nested structure
    if (createdAtFilter.created_at) {
      cleanFilters.exports = {
        ...cleanFilters.exports,
        created_at: createdAtFilter.created_at,
      };
    }

    return {
      cleanFilters,
      formularyIds,
      formularyGroupIds,
      exportType,
    };
  }, [byKey]);

  const {
    cleanFilters,
    formularyIds,
    formularyGroupIds,
    exportType,
  } = processedFilters;

  // Memoize query variables to prevent unnecessary refetches
  const queryVariables = React.useMemo(
    () => ({
      first: 50,
      filters: cleanFilters,
      formularyIds,
      formularyGroupIds,
      exportType,
    }),
    [cleanFilters, formularyIds, formularyGroupIds, exportType],
  );

  const { data, fetchMore, loading, refetch, variables } = useQuery(
    exportQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: queryVariables,
      skip: !user?.id,
      notifyOnNetworkStatusChange: true,
    },
  );

  // Memoize notification subscription to avoid recreating on every render
  React.useEffect(() => {
    const unsub = NotificationsTopics.subscribeTopic('Export', () => {
      refetch(queryVariables);
    });

    return () => {
      unsub();
    };
  }, [refetch, queryVariables]);

  const pageInfo = React.useMemo(() => data?.exports.pageInfo, [
    data?.exports.pageInfo,
  ]);

  const onFetchMore = React.useCallback(() => {
    if (!pageInfo?.hasNextPage) return;

    fetchMore({
      variables: {
        after: pageInfo.endCursor,
        first: 50,
      },
    });
  }, [fetchMore, pageInfo?.endCursor, pageInfo?.hasNextPage]);

  const exports = React.useMemo(
    () => data?.exports?.edges?.map((e: any) => e.node) || [],
    [data?.exports?.edges],
  );

  return {
    onFetchMore,
    pageInfo,
    loading,
    exports,
  };
}

type ExportContextvalue = ReturnType<typeof useExportsController>;

export const [ExportContext, useExports] = createContext<ExportContextvalue>();

export function ExportsProvider({ children }: { children?: React.ReactNode }) {
  return (
    <ExportContext value={useExportsController()}>{children}</ExportContext>
  );
}
